import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLessonState } from '../../hooks/useLessonState';
import {
  useAssessmentApi,
  type DebriefResponse,
} from '../../hooks/useAssessmentApi';
import { useAutoContinue } from '../../hooks/useAutoContinue';
import { useAssessmentFlow } from '../../hooks/useAssessmentFlow';
import { startContext } from '../../hooks/useStartContext';
import TurnCardView from '../turncard/TurnCardView';
import { apiFetch } from '../../api/client';
import type { WordConfidence } from '../../speech';
import { isStartable, type PlanContext } from '../../api/plan';

/** What the next-lesson picker needs from /plan/today (a subset of the shared response). */
interface NextContextPlan {
  tracks: Array<{ contexts: Array<Pick<PlanContext, 'context_id' | 'state'>> }>;
}

// --- Plan / Next Lesson helpers ----------------------------------------------

/** Internal: one round-trip to /plan/today (a 401 is handled by the auth layer). */
async function _fetchPlanTodayOnce(
  signal?: AbortSignal
): Promise<Response | null> {
  return apiFetch('/plan/today', { signal });
}

/**
 * Fetch /plan/today, with one automatic retry on 5xx after a short backoff.
 *
 * Why the 5xx retry: the post-checkin debrief makes a one-shot /plan/today
 * call to decide whether to show "Next Lesson". A single transient backend
 * blip (e.g. Prisma engine respawning, gRPC fd issues bleeding into the
 * connection layer) would otherwise permanently hide the button for that
 * screen with no recovery path short of navigating away. One retry catches
 * the vast majority of these without adding meaningful latency.
 *
 * Returns null on 404 (treated as "no plan", not an error), on retry-exhausted
 * 5xx, or on any other non-OK status — caller hides Next Lesson.
 */
async function fetchPlanToday(
  signal?: AbortSignal
): Promise<NextContextPlan | null> {
  let response = await _fetchPlanTodayOnce(signal);
  if (response === null) return null;
  if (response.status >= 500 && response.status < 600) {
    // Wait briefly, then retry once. Keeps post-checkin UX robust to one-off
    // backend hiccups without making failure modes invisible (still bails on
    // second 5xx).
    await new Promise((resolve) => setTimeout(resolve, 400));
    if (signal?.aborted) return null;
    response = await _fetchPlanTodayOnce(signal);
    if (response === null) return null;
  }
  if (response.status === 404) return null;
  if (!response.ok) return null;
  return (await response.json()) as NextContextPlan;
}

/**
 * Pick the next context_id to offer as "Next Lesson" after a completed check-in.
 *
 * Rule:
 *   1. If a track contains the just-completed `context_id`, return the first
 *      startable (available or in-progress) context in that track other than
 *      the completed one, or null if there is none.
 *   2. Otherwise, fall back to the first startable context in any track.
 *   3. If nothing is startable (end of track / no plan), return null and the
 *      caller hides the button.
 */
export function pickNextContextId(
  plan: NextContextPlan | null,
  completedContextId: string | null
): string | null {
  if (!plan || plan.tracks.length === 0) return null;

  if (completedContextId) {
    const track = plan.tracks.find((t) =>
      t.contexts.some((c) => c.context_id === completedContextId)
    );
    if (track) {
      const next = track.contexts.find(
        (c) => isStartable(c) && c.context_id !== completedContextId
      );
      return next ? next.context_id : null;
    }
  }

  for (const track of plan.tracks) {
    const next = track.contexts.find(
      (c) => isStartable(c) && c.context_id !== completedContextId
    );
    if (next) return next.context_id;
  }
  return null;
}

// --- Component ----------------------------------------------------------------

export const AssessmentRunner: React.FC = () => {
  // --- All hook declarations FIRST (Rules of Hooks) ---
  const { contextId } = useParams<{ contextId: string }>();
  const navigate = useNavigate();
  const state = useLessonState();
  const api = useAssessmentApi();
  const autoContinue = useAutoContinue();
  const {
    startAssessment,
    resumeOffer,
    resumed,
    continueRun,
    restartRun,
    handleNextStep,
    abortControllerRef,
  } = useAssessmentFlow(contextId, state, api, autoContinue);

  const [debrief, setDebrief] = useState<DebriefResponse | null>(null);
  const [debriefLoading, setDebriefLoading] = useState(false);
  const [nextContextId, setNextContextId] = useState<string | null>(null);
  const [nextLessonStarting, setNextLessonStarting] = useState(false);
  // Avoid re-fetching debrief if the lessonComplete effect re-runs.
  const debriefFetchedForRunRef = useRef<string | null>(null);

  // --- Effects after declarations ---

  // Reset all debrief-related local state. Used by both the contextId-change
  // effect and handleRetry so the post-checkin screen never shows stale data
  // from a previous run.
  const resetDebriefState = React.useCallback((): void => {
    setDebrief(null);
    setDebriefLoading(false);
    setNextContextId(null);
    setNextLessonStarting(false);
    debriefFetchedForRunRef.current = null;
  }, []);

  // Reset on contextId change and start assessment
  useEffect(() => {
    // Optional-chain abort: cheaper to read than `if (current) current.abort()`
    // and avoids an otherwise-impossible-to-test defensive branch.
    abortControllerRef.current?.abort();
    state.reset();
    resetDebriefState();

    abortControllerRef.current = new AbortController();
    startAssessment(abortControllerRef.current.signal);

    return (): void => {
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
      state.hasStartedRef.current = false;
    };
    // Intentionally only depend on contextId to reset on route change
  }, [contextId]);

  // S7.4: When the check-in completes, fetch the debrief and compute "Next Lesson".
  // Note: deps are intentionally limited to the trigger inputs (lessonComplete +
  // runId). We read `api` and `contextId` via refs to avoid the effect re-running
  // on every render (`useAssessmentApi()` returns a fresh object each render),
  // which would otherwise cancel in-flight fetches on the next re-render.
  const apiRef = useRef(api);
  apiRef.current = api;
  const contextIdRef = useRef(state.contextId);
  contextIdRef.current = state.contextId;
  useEffect(() => {
    if (!state.lessonComplete || !state.runId) return;
    if (debriefFetchedForRunRef.current === state.runId) return;
    debriefFetchedForRunRef.current = state.runId;

    let cancelled = false;
    const localAbort = new AbortController();
    setDebriefLoading(true);

    // Run debrief and plan fetches in parallel — they're independent, and
    // running them sequentially produced a visible UX gap where the debrief
    // panel rendered without the "Next Lesson" button, which then popped in
    // a moment later. A user who clicked "Back to Today" in that gap would
    // never see the affordance.
    const debriefPromise = apiRef.current
      .fetchDebrief(state.runId!, localAbort.signal)
      .then((data) => {
        if (!cancelled) setDebrief(data);
      })
      .catch((err) => {
        // Leave panel empty on failure; "Back to Today" still works.
        console.error('[AssessmentRunner] fetchDebrief failed:', err);
      })
      .finally(() => {
        if (!cancelled) setDebriefLoading(false);
      });

    const planPromise = fetchPlanToday(localAbort.signal)
      .then((plan) => {
        if (cancelled) return;
        setNextContextId(pickNextContextId(plan, contextIdRef.current));
      })
      .catch((err) => {
        console.error('[AssessmentRunner] fetchPlanToday failed:', err);
        if (!cancelled) setNextContextId(null);
      });

    // void to satisfy no-floating-promises; cleanup handles cancellation.
    void Promise.all([debriefPromise, planPromise]);

    return (): void => {
      cancelled = true;
      localAbort.abort();
      // Clear the ref so the next effect run (e.g. React 18 StrictMode's
      // double-invocation in dev) actually re-issues the fetch instead of
      // early-returning at the ref-guard check. In production this cleanup
      // only fires on unmount or deps change, where re-fetching is correct.
      debriefFetchedForRunRef.current = null;
    };
  }, [state.lessonComplete, state.runId]);

  // --- Callbacks ---
  const handleTranscript = React.useCallback(
    async (result: {
      transcript: string;
      confidence: number | null;
      words?: WordConfidence[];
    }): Promise<void> => {
      if (!state.runId || !state.stepVersion) {
        console.warn(
          '[AssessmentRunner] Cannot process transcript: missing runId or stepVersion'
        );
        return;
      }

      try {
        state.setProcessing(true);
        // The answer is in; the next step decides whether the mic comes back.
        state.setAwaitingInput(false);

        // #204 P1: show the learner's transcribed speech as a message so
        // the exchange (including retries) stays visible in the conversation.
        state.addMessage({
          id: crypto.randomUUID(),
          sender: 'user',
          content: result.transcript,
          timestamp: new Date().toISOString(),
          ...(result.words ? { words: result.words } : {}),
        });

        await handleNextStep(state.runId, state.stepVersion, {
          transcript: result.transcript,
          confidence: result.confidence,
        });
      } catch (err) {
        console.error('[AssessmentRunner] Error processing transcript:', err);
        state.setError(
          err instanceof Error
            ? err.message
            : 'Failed to process audio. Please try again.'
        );
      } finally {
        state.setProcessing(false);
      }
    },
    [
      state.runId,
      state.stepVersion,
      state.setProcessing,
      state.setError,
      state.setAwaitingInput,
      handleNextStep,
    ]
  );

  const handleSttError = React.useCallback(
    (error: { code: string; message: string }): void => {
      if (error.code === 'aborted' || error.code === 'no-speech') {
        // BR-011: nothing recognized (stopped or timed out). MicButton returns
        // to idle; invite another try without restarting the check-in.
        state.addMessage({
          id: crypto.randomUUID(),
          sender: 'tutor',
          content: "I didn't catch that — tap the mic and try again.",
          timestamp: new Date().toISOString(),
          stepKind: 'no_speech',
        });
        return;
      }
      if (error.code === 'mic-unavailable') {
        // MicButton shows a Reload button in place of the mic (#254).
        return;
      }
      state.setError(error.message);
    },
    [state]
  );

  // Re-runs an automatic advance that failed (#250).
  const handleRetryAdvance = React.useCallback(() => {
    if (!state.runId || !state.stepVersion) {
      return;
    }
    void handleNextStep(state.runId, state.stepVersion);
  }, [state.runId, state.stepVersion, handleNextStep]);

  // Re-fetches the natural clip of a check-in line whose audio failed to load.
  const handleRetryAudio = React.useCallback(
    async (messageId: string): Promise<boolean> => {
      const target = state.messages.find((msg) => msg.id === messageId);
      if (!state.runId || !target?.audioText || !target.audioLocale) {
        return false;
      }
      try {
        const natural = await apiRef.current.fetchTextAudioUrl(
          state.runId,
          target.audioText,
          target.audioLocale,
          target.audioSpeed ?? 'natural',
          abortControllerRef.current?.signal
        );
        state.setMessages((prev) =>
          prev.map((msg) =>
            msg.id === messageId
              ? { ...msg, audioUrls: { natural }, audioUnavailable: undefined }
              : msg
          )
        );
        return true;
      } catch (error) {
        console.error('[AssessmentRunner] Audio retry failed:', error);
        return false;
      }
    },
    [state.runId, state.messages, state.setMessages, abortControllerRef]
  );

  const handleRetry = (): void => {
    state.reset();
    state.setLoading(true);
    // Retry should also clear post-checkin state so a stale debrief or stale
    // "Next Lesson" target doesn't survive into the new run.
    resetDebriefState();
    abortControllerRef.current?.abort();
    abortControllerRef.current = new AbortController();
    startAssessment(abortControllerRef.current.signal, true);
  };

  const handleBackToToday = React.useCallback((): void => {
    navigate('/');
  }, [navigate]);

  const handleNextLesson = async (): Promise<void> => {
    if (!nextContextId) return;
    setNextLessonStarting(true);
    try {
      const newLessonId = await startContext(nextContextId);
      navigate(`/lesson/${newLessonId}`);
    } catch (err) {
      console.error('[AssessmentRunner] Failed to start next lesson:', err);
    } finally {
      // Clear the loading flag regardless of outcome. On success the component
      // typically unmounts before this runs (navigation tears down AssessmentRunner),
      // but if `navigate` is ever intercepted (router guard, auth refresh, future
      // route that keeps the debrief mounted) the button would otherwise stay
      // disabled forever with no recovery path.
      setNextLessonStarting(false);
    }
  };

  // "?" on a persona line (BR-043): what did the character just say?
  const handleAskHelp = React.useCallback(
    (formId: string | null) => {
      if (!state.runId) return Promise.reject(new Error('No active run'));
      return apiRef.current.askHelp(state.runId, formId);
    },
    [state.runId]
  );

  return (
    <TurnCardView
      mode="checkin"
      loading={state.loading}
      processing={state.processing}
      error={state.error}
      messages={state.messages}
      parts={state.parts}
      contextName={state.contextName}
      lessonComplete={state.lessonComplete}
      loadingMessage="Starting check-in..."
      onRetry={handleRetry}
      onBackFromError={handleBackToToday}
      onLeave={handleBackToToday}
      onStartRecording={() => {}}
      onStopRecording={() => {}}
      onTranscript={handleTranscript}
      onSttError={handleSttError}
      languageCode={state.languageCode ?? undefined}
      runId={state.runId}
      stepIndex={state.stepIndex}
      onAudioEnded={undefined}
      onRetryAudio={handleRetryAudio}
      onAskHelp={handleAskHelp}
      awaitingInput={state.awaitingInput}
      advanceFailed={state.advanceFailed}
      onRetryAdvance={handleRetryAdvance}
      resumeOffer={resumeOffer}
      resumed={resumed}
      onContinue={() => void continueRun(abortControllerRef.current?.signal)}
      onRestart={() => void restartRun(abortControllerRef.current?.signal)}
      debrief={debrief}
      debriefLoading={debriefLoading}
      onNextLesson={nextContextId ? handleNextLesson : undefined}
      nextLessonStarting={nextLessonStarting}
    />
  );
};

export default AssessmentRunner;
